import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { EmailSectionLayout } from '../../components/EmailSectionLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, Plus, Loader2, Trash2, Search, Upload, FileSpreadsheet, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TINT = {
  active: 'bg-emerald-100 text-emerald-700',
  unsubscribed: 'bg-zinc-200 text-zinc-600',
  bounced: 'bg-red-100 text-red-700',
  complained: 'bg-red-100 text-red-700',
};

export const EmailSubscribersPage = () => {
  const { api } = useAuth();
  const [params, setParams] = useSearchParams();
  const [subs, setSubs] = useState([]);
  const [total, setTotal] = useState(0);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const listFilter = params.get('list') || '';
  const statusFilter = params.get('status') || '';

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', tags: '', list_ids: [] });
  const [saving, setSaving] = useState(false);

  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams();
      if (q) qs.set('q', q);
      if (listFilter) qs.set('list', listFilter);
      if (statusFilter) qs.set('status', statusFilter);
      const [s, l] = await Promise.all([
        api.get(`/email/subscribers?${qs.toString()}`),
        api.get('/email/lists'),
      ]);
      setSubs(s.data?.subscribers || []);
      setTotal(s.data?.total || 0);
      setLists(l.data?.lists || []);
    } finally { setLoading(false); }
  }, [api, q, listFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const addSub = async () => {
    if (!form.email.trim()) return toast.error('Email is required');
    setSaving(true);
    try {
      await api.post('/email/subscribers', {
        email: form.email.trim(),
        first_name: form.first_name,
        last_name: form.last_name,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        list_ids: form.list_ids,
      });
      toast.success('Subscriber added');
      setShowAdd(false);
      setForm({ email: '', first_name: '', last_name: '', tags: '', list_ids: [] });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add'); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.email}?`)) return;
    try { await api.delete(`/email/subscribers/${s.id}`); toast.success('Deleted'); await load(); }
    catch { toast.error('Delete failed'); }
  };

  const setFilter = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next);
  };

  return (
    <EmailSectionLayout
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-csv-btn"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Import CSV</Button>
          <Button onClick={() => setShowAdd(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-subscriber-btn"><Plus className="w-4 h-4 mr-1.5" /> Add subscriber</Button>
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name…" className="pl-9" data-testid="subscriber-search" />
        </div>
        <Select value={listFilter || 'all'} onValueChange={(v) => setFilter('list', v === 'all' ? '' : v)}>
          <SelectTrigger className="w-44" data-testid="list-filter"><SelectValue placeholder="All lists" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All lists</SelectItem>
            {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter || 'all'} onValueChange={(v) => setFilter('status', v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue placeholder="Any status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : subs.length === 0 ? (
        <Card data-testid="subscribers-empty"><CardContent className="py-14 text-center text-zinc-500">
          <Users className="w-10 h-10 mx-auto text-zinc-300 mb-3" />
          <p className="font-medium text-zinc-700">No subscribers found</p>
          <p className="text-sm">Add people manually or import a CSV to get started.</p>
        </CardContent></Card>
      ) : (
        <Card data-testid="subscribers-table">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-left text-zinc-500">
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Tags</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`subscriber-row-${s.id}`}>
                      <td className="px-4 py-3 font-medium text-zinc-800">{s.email}</td>
                      <td className="px-4 py-3 text-zinc-600">{[s.first_name, s.last_name].filter(Boolean).join(' ') || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(s.tags || []).slice(0, 3).map((t) => <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>)}
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_TINT[s.status] || 'bg-zinc-100 text-zinc-600'}`}>{s.status}</span></td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => remove(s)} className="p-1.5 text-zinc-400 hover:text-red-600" data-testid={`delete-subscriber-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="text-xs text-zinc-400">{total} total</div>

      {/* Add subscriber dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-subscriber-dialog">
          <DialogHeader><DialogTitle>Add subscriber</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" data-testid="sub-email-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>First name</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="sub-fname-input" /></div>
              <div className="space-y-1.5"><Label>Last name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="vip, lead-magnet" data-testid="sub-tags-input" /></div>
            {lists.length > 0 && (
              <div className="space-y-1.5">
                <Label>Add to list</Label>
                <Select value={form.list_ids[0] || 'none'} onValueChange={(v) => setForm({ ...form, list_ids: v === 'none' ? [] : [v] })}>
                  <SelectTrigger data-testid="sub-list-select"><SelectValue placeholder="No list" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No list</SelectItem>
                    {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={addSub} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-subscriber-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportDialog open={showImport} onClose={() => setShowImport(false)} lists={lists} onDone={load} />
    </EmailSectionLayout>
  );
};

const ImportDialog = ({ open, onClose, lists, onDone }) => {
  const { api } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [listId, setListId] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); setListId(''); };

  const onPick = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post('/email/subscribers/bulk-import?dry_run=true', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not read CSV'); reset(); }
    finally { setBusy(false); }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const qs = `dry_run=false${listId ? `&list_id=${listId}` : ''}`;
      const r = await api.post(`/email/subscribers/bulk-import?${qs}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${r.data.imported} subscriber${r.data.imported === 1 ? '' : 's'}`);
      reset(); onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Import subscribers from CSV</DialogTitle>
          <DialogDescription>CSV needs an <span className="font-mono">email</span> column. Optional: <span className="font-mono">first_name</span>, <span className="font-mono">last_name</span>, <span className="font-mono">tags</span>.</DialogDescription>
        </DialogHeader>
        {!preview ? (
          <label className="block border-2 border-dashed border-zinc-300 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-400 transition-colors" data-testid="csv-dropzone">
            <input type="file" accept=".csv" className="hidden" onChange={(e) => onPick(e.target.files?.[0])} />
            {busy ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" /> : (
              <>
                <Upload className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
                <div className="text-sm font-medium text-zinc-700">Click to choose a CSV file</div>
              </>
            )}
          </label>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2 text-sm">
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100"><CheckCircle2 className="w-3 h-3 mr-1" /> {preview.valid} new</Badge>
              {preview.duplicates > 0 && <Badge variant="secondary">{preview.duplicates} duplicate</Badge>}
              {preview.errors?.length > 0 && <Badge className="bg-red-100 text-red-700 hover:bg-red-100"><X className="w-3 h-3 mr-1" /> {preview.errors.length} error</Badge>}
            </div>
            <div className="max-h-48 overflow-y-auto border border-zinc-200 rounded-lg">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-zinc-500 border-b border-zinc-200"><th className="px-3 py-2">Email</th><th className="px-3 py-2">Name</th><th className="px-3 py-2"></th></tr></thead>
                <tbody>
                  {(preview.preview || []).slice(0, 50).map((r, i) => (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="px-3 py-1.5">{r.email}</td>
                      <td className="px-3 py-1.5 text-zinc-500">{[r.first_name, r.last_name].filter(Boolean).join(' ')}</td>
                      <td className="px-3 py-1.5">{r.duplicate && <span className="text-amber-600">dup</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {lists.length > 0 && (
              <Select value={listId || 'none'} onValueChange={(v) => setListId(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="import-list-select"><SelectValue placeholder="Add imported to a list (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Don&apos;t add to a list</SelectItem>
                  {lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          {preview && <Button onClick={doImport} disabled={busy || preview.valid === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-import-btn">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Import {preview.valid}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailSubscribersPage;
